<script>
import CalendarResource from '@/resources/calendar'
import ReservationResource from '@/resources/reservation'
import moment from 'moment'
import messages from '@/mixins/messages'
import DateStepper from './DateStepper.vue'

export default {
    name: 'CalendarReservationsSimple',
    components: { DateStepper },
    computed: {
        locationCurrencySymbol() {
            const locationId = this.calendarResource?.attributes?.location
            if (!locationId) return this.$store.getters.currencySymbol
            const location = this.$store.state.locationResources.find((l) => l.id == locationId)
            return location?.attributes?.currency_symbol || '$'
        },
        showDatePicker() {
            return !this.forDate
        },
        bookDateFormattedPretty() {
            return moment(this.bookDate).format('dddd, MMMM Do')
        },
        bookDateFormatted() {
            return moment(this.bookDate).format('YYYY-MM-DD')
        },
        bookTimeDateFormattedPretty() {
            if(!this.bookTimeSlotResource) {
                return this.bookDateFormattedPretty
            }
            return moment(this.bookTimeSlotResource.dateTime).format('dddd, MMMM Do \@ h:mm a')
        },
        selectedReservationsHashKeys() {
            return this.selectedReservations.map((reserv) => reserv.toHashKey())
        },
        blockedSlotIndices() {
            const blockLength = this.calendarResource ? (this.calendarResource.get('reservation_block_length') || 1) : 1
            const before = new Set()
            const after = new Set()

            if (blockLength > 1) {
                const slots = this.allReservationResourcesForSelectedDay
                const availSlotNumbers = new Set(this.availReservationResources.map(r => r.get('slot')))

                // Slot format: YYYYWWDBB — prefix is YYYYWWD (7 chars), BB is last 2 chars
                const blockBefore = (slotStr) => {
                    const prefix = slotStr.slice(0, 7)
                    const baseBB = parseInt(slotStr.slice(7), 10)
                    for (let j = 1; j < blockLength; j++) {
                        const precedingBB = baseBB - j
                        if (precedingBB < 0) break
                        const precedingSlot = prefix + String(precedingBB).padStart(2, '0')
                        const precedingIdx = slots.findIndex(s => s.get('slot') === precedingSlot)
                        if (precedingIdx !== -1) before.add(precedingIdx)
                    }
                }

                // Intrinsically disabled slots: only propagate backwards (overlap prevention).
                // No after propagation — secondary highlight is reserved for active selections only.
                for (const reserv of slots) {
                    if (reserv.attributes.user || reserv.get('is_blackout')) {
                        blockBefore(String(reserv.get('slot')))
                    }
                }

                // Selected slots: propagate both directions.
                // After stops at the first availability gap; before is always slot-number based.
                for (const reserv of this.selectedReservations) {
                    const idx = slots.findIndex(s => s.toHashKey() === reserv.toHashKey())
                    if (idx !== -1) {
                        const selectedSlot = String(reserv.get('slot'))
                        const prefix = selectedSlot.slice(0, 7)
                        const baseBB = parseInt(selectedSlot.slice(7), 10)

                        blockBefore(selectedSlot)

                        for (let j = 1; j < blockLength; j++) {
                            const expectedSlot = prefix + String(baseBB + j).padStart(2, '0')
                            if (!availSlotNumbers.has(expectedSlot)) break
                            const displayIdx = slots.findIndex(s => s.get('slot') === expectedSlot)
                            if (displayIdx !== -1) after.add(displayIdx)
                        }
                    }
                }
            }

            return { before, after }
        },
        allReservationResourcesForSelectedDay() {
            let selectedBookDate = this.bookDate
            let selectedDayExistingResources = this.reservationResources.filter((resource) => {
                let resourceDate = new Date(resource.get('time'))
                let bookYearIsSame = selectedBookDate.getFullYear() == resourceDate.getFullYear()
                let bookMonthIsSame = selectedBookDate.getMonth() == resourceDate.getMonth()
                let bookDateIsSame = selectedBookDate.getDate() == resourceDate.getDate()
                return bookYearIsSame && bookMonthIsSame && bookDateIsSame
            })

            let avails = this.availReservationResources.concat(selectedDayExistingResources).sort((resLeft, resRight) => {
                return parseInt(resLeft.get('slot')) - parseInt(resRight.get('slot'))
            }).filter((resource) => {
                const timeContext = resource.getTimeContext(10)
                return timeContext.isFuture
            })

            if(this.forDate instanceof Date && this.forDate.getHours() !== 0) {
                // Date provided is more specific than just a day -- it's for a time
                // So instead of showing all available, only show that one slot
                let forTimeReservationResource = avails.find((reserv) => new Date(reserv.dateTime).getTime() == this.forDate.getTime())
                avails = (forTimeReservationResource) ? [forTimeReservationResource] : []
            }

            return avails
        }
    },
    mixins: [messages],
    watch: {
        forDate() {
            this.bookDate = this.forDate
            this.bookDateChange()
        },
    },
    props: {
        calendarId: {
            type: String
        },
        forDate: {
            type: [String, Date]
        },
        many: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const maxDate = new Date(now.getFullYear(), now.getMonth() + 2, 0)
        const minDate = new Date(now.getFullYear(), now.getMonth() - 2, 0)
        return {
            calendarResource: null,
            selectedReservations: [],
            userId: this.$store.getters.userId,
            reservationResources: [],
            availReservationResources: [],
            loading: true,
            loadingReservationTimes: false,
            refreshIntervalId: null,
            refreshIntervalMs: 6e4,
            bookDate: this.forDate || today,
            dateMin: minDate,
            dateMax: maxDate,
            now
        }
    },
    methods: {
        dateDisabled(ymd, date) {
            return false
        },
        timestampTimeFormattedPretty(timeStampOrDateInstance) {
            return moment(timeStampOrDateInstance).format('h:mm a')
        },
        timestampDayFormattedPretty(timeStampOrDateInstance) {
            return moment(timeStampOrDateInstance).format('dddd, MMMM Do')
        },
        refreshBookDate() {
            if(this.$refs.calendar) {
                this.$refs.calendar.$emit('input', this.bookDate)
            }
        },
        getTableRowClassForReservation(reserv) {
            let classes = []
            if(!reserv.id || reserv.get('is_blackout')) {
                classes.push('empty')
            } else {
                classes.push('reserved')
            }

            return classes
        },
        async refresh() {
            this.loading = true
            let thisBookDate = this.bookDate
            this.bookDate = new Date()
            this.bookDate = thisBookDate
            await this.getExistingReservations()
            await this.bookDateChange()
            this.loading = false
        },
        async getExistingReservations() {
            this.loading = true
            let reservationResources = await ReservationResource.filterAll({ future: true, calendar: this.calendarId })
            this.reservationResources = reservationResources
            this.loading = false
            return this.reservationResources
        },
        onStepperInput(value) {
            this.bookDate = value
            this.bookDateChange()
        },
        async bookDateChange() {
            this.loadingReservationTimes = true
            let availResources = await this.calendarResource.getQuote(this.bookDateFormatted)
            this.availReservationResources = availResources
            this.loadingReservationTimes = false
        },
        calendarIsAvailableForSlot(slot) {
            for(let i in this.availReservationResources) {
                if(this.availReservationResources[i].get('slot') === slot) {
                    return true
                }
            }

            return false
        },
        onSelect(reservation) {
            if(!this.many) {
                this.selectedReservations = [reservation]
            } else {
                let hashKey = reservation.toHashKey()
                let idx = this.selectedReservationsHashKeys.indexOf(hashKey)

                if(~idx) {
                    this.$delete(this.selectedReservations, idx)
                } else {
                    this.selectedReservations.push(reservation)
                }
            }

            this.$emit('input', this.selectedReservations)
        }
    },
    async mounted() {
        this.calendarResource = await CalendarResource.detail(this.calendarId)
        this.getExistingReservations()
        this.loading = false
        this.$nextTick(() => {
            this.bookDateChange()
            this.refreshBookDate()
            this.refreshIntervalId = setInterval(() => this.refresh(), this.refreshIntervalMs)
        })
    },
    beforeDestroy() {
        clearInterval(this.refreshIntervalId)
    }
}
</script>

<template>
    <div>
        <b-row v-if="calendarResource">
            <b-col cols="12">
                <h5>{{ calendarResource.get('name') }}</h5>
                <h6 class="text-muted"><a href="javascript:{}" v-b-modal="`simulator-desc-${calendarResource.id}`">{{ calendarResource.get('type_name') }} Simulateur <b-icon icon="question-circle" /></a></h6>
                <h6 class="text-muted"><strong>{{ locationCurrencySymbol }}{{ moneyFormat(calendarResource.get('cost_cents')) }}</strong> {{ calendarResource.get('cost_unit') }}</h6>
                <h6 class="text-muted">{{ timestampDayFormattedPretty(bookDate) }}</h6>
            </b-col>

            <b-col md="4" class="mt-2 mb-5" v-if="showDatePicker">
                <b-card bg-variant="light" class="shadow">
                    <date-stepper class="d-block" :value="bookDate" :min="dateMin" :max="dateMax" :disabled-fn="dateDisabled" ref="calendar" @input="onStepperInput" />
                </b-card>
            </b-col>

            <b-col class="mt-2 mb-5">
                <div v-if="messages.length > 0">
                    <b-alert :variant="message.variant" show v-for="(message, i) in messages" :key="i">{{ message.text }}</b-alert>
                </div>

                <div id="time-pane">

                    <b-row v-if="!loadingReservationTimes && allReservationResourcesForSelectedDay.length > 0">
                        <b-col cols="12" v-for="(reserv, i) in allReservationResourcesForSelectedDay" :key="i" :class="getTableRowClassForReservation(reserv)" :data-slot="reserv.attributes.slot">
                            <b-button v-if="reserv.attributes.user == userId && reserv.attributes.paid" size="sm" variant="success" class="mb-1" block disabled>
                                {{ timestampTimeFormattedPretty(reserv.get('time')) }} (Vous, confirmé)
                            </b-button>
                            <b-button v-else-if="reserv.attributes.user == userId && !reserv.attributes.paid && !reserv.get('is_blackout')" size="sm" variant="warning" class="mb-1" block disabled>
                                {{ timestampTimeFormattedPretty(reserv.get('time')) }} (Vous, en attente)
                            </b-button>
                            <b-button v-else block :disabled="!!reserv.attributes.user || reserv.get('is_blackout') || blockedSlotIndices.before.has(i) || blockedSlotIndices.after.has(i)" size="sm" :variant="selectedReservationsHashKeys.includes(reserv.toHashKey()) || blockedSlotIndices.after.has(i) ? 'primary' : 'outline-primary'" class="mb-1" @click="onSelect(reserv)">
                                {{ timestampTimeFormattedPretty(reserv.get('time')) }} <span v-if="reserv.attributes.price_cents !== undefined">&mdash; {{ locationCurrencySymbol }}{{ moneyFormat(reserv.get('price_cents')) }}</span>
                            </b-button>
                        </b-col>
                    </b-row>

                    <b-alert variant="warning" show v-else-if="!loadingReservationTimes">
                        <strong>Aucune disponibilité</strong> pour la date sélectionnée.
                    </b-alert>
                    
                    <p v-else class="text-center">Chargement...</p>

                    <slot name="calendar-body" :reservations="allReservationResourcesForSelectedDay" />
                </div>

            </b-col>
        </b-row>

        <b-row v-else>
            Chargement...
        </b-row>

        <!-- MODALS -->
        <b-modal :id="`simulator-desc-${calendarResource.id}`" v-if="calendarResource" :title="`${calendarResource.get('type_name')} Simulateur`" ok-only no-fade centered>
            <b-embed v-if="calendarResource.get('type_name').match(/haptic/gi)" type="iframe" aspect="16by9" src="https://www.youtube.com/embed/S-pldT_FWck" allowfullscreen />
            <p class="my-4">{{ calendarResource.get('type_description') }}</p>
        </b-modal>
    </div>
</template>

<style lang="scss">
#time-pane {
    overflow-y: scroll;
    overflow-x: hidden;
    
    table {
        width: 100%;

        tr {
            &.empty {
                td {
                    color: #ccc;
                }
            }

            &.reserved {
                border-left: 3px solid #34beed;
                font-weight: bold;

                a {
                    font-weight: normal;
                }
            }
        }
    }
}
</style>
